import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import contentApi from "../../services/contentApi";
import "./BlogAdmin.css";

const EMPTY_PAGE = { count: 0, next: null, previous: null, results: [] };

const normalizeChannel = (value) => String(value || "").trim().toLowerCase();
const isInsuranceChannel = (value) => {
  const channel = normalizeChannel(value);
  return !channel || channel === "insurance";
};
const channelLabel = (value) => {
  const channel = normalizeChannel(value);
  return channel
    ? `${channel.charAt(0).toUpperCase()}${channel.slice(1)}`
    : "Insurance";
};

const formatDate = (value, fallback = "Not set") => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-NG", { dateStyle: "medium" }).format(date)
    : fallback;
};

const postStatus = (post) => {
  if (post.status === "published" && new Date(post.published_at).getTime() > Date.now()) {
    return { value: "scheduled", label: "Scheduled" };
  }
  const value = ["published", "archived"].includes(post.status) ? post.status : "draft";
  return { value, label: `${value[0].toUpperCase()}${value.slice(1)}` };
};

const BlogAdmin = ({ token }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [reload, setReload] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [notice, setNotice] = useState(null);
  const [state, setState] = useState({
    status: "loading",
    data: EMPTY_PAGE,
    message: "",
    channelMismatch: 0,
  });
  const params = useMemo(() => ({
    page,
    page_size: 25,
    ...(query ? { q: query } : {}),
    ...(status ? { status } : {}),
  }), [page, query, status]);

  useEffect(() => {
    let current = true;
    setState((previous) => ({ ...previous, status: "loading", message: "" }));
    contentApi.getPosts(params, token).then((data) => {
      if (!current) return;
      const received = Array.isArray(data?.results) ? data.results : [];
      const channelMismatch = received.filter(
        (post) => !isInsuranceChannel(post?.channel)
      ).length;
      setState({
        status: "ready",
        message: "",
        channelMismatch,
        data: {
          count: Number(data?.count) || 0,
          next: data?.next || null,
          previous: data?.previous || null,
          results: received.filter((post) => isInsuranceChannel(post?.channel)),
        },
      });
    }).catch((error) => {
      if (current) setState((previous) => ({
        ...previous,
        status: "error",
        message: error?.message || "Insurance posts could not be loaded.",
      }));
    });
    return () => { current = false; };
  }, [params, reload, token]);

  const removePost = async (post) => {
    if (pendingDelete !== post.id) {
      setPendingDelete(post.id);
      return;
    }
    if (deleting !== null) return;
    setDeleting(post.id);
    try {
      await contentApi.deletePost(post.id, token);
      setNotice({ type: "success", message: `${post.title} was permanently deleted.` });
      setPendingDelete(null);
      setReload((value) => value + 1);
    } catch (error) {
      setNotice({ type: "error", message: error?.message || "The post could not be deleted." });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="blog-admin-page">
      <header className="travel-admin-page-heading blog-admin-page-heading">
        <div>
          <p>Insurance content studio</p>
          <h1>Blog posts</h1>
          <span>Create insurance guidance, manage drafts, and control when articles appear.</span>
        </div>
        <div className="travel-admin-page-heading__actions">
          <Link className="travel-admin-button travel-admin-button--secondary" to="/content-admin/analytics">
            <i className="fas fa-chart-line" aria-hidden="true" /> Analytics
          </Link>
          <Link className="travel-admin-button" to="/content-admin/blog/new">
            <i className="fas fa-plus" aria-hidden="true" /> New post
          </Link>
        </div>
      </header>

      {notice && <div className={`blog-admin-notice blog-admin-notice--${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
        <span>{notice.message}</span>
        <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss message"><i className="fas fa-xmark" aria-hidden="true" /></button>
      </div>}

      {state.channelMismatch > 0 && (
        <div className="blog-admin-notice blog-admin-notice--error blog-admin-channel-warning" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <span>
            {state.channelMismatch} unexpected non-insurance {state.channelMismatch === 1 ? "post was" : "posts were"} hidden. Refresh or contact an administrator if this continues.
          </span>
        </div>
      )}

      <section className="travel-admin-panel blog-admin-library" aria-labelledby="insurance-library-title">
        <div className="blog-admin-library__heading">
          <div><p>Editorial library</p><h2 id="insurance-library-title">Insurance stories</h2></div>
          <span>{state.data.count.toLocaleString("en-NG")} total</span>
        </div>
        <div className="blog-admin-tools">
          <form role="search" onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(queryInput.trim()); }}>
            <label htmlFor="content-post-search">Search posts</label>
            <div><i className="fas fa-magnifying-glass" aria-hidden="true" /><input id="content-post-search" type="search" value={queryInput} onChange={(event) => setQueryInput(event.target.value)} placeholder="Title, excerpt, or content" /><button type="submit">Search</button></div>
          </form>
          <label className="blog-admin-status-filter"><span>Status</span><select value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}><option value="">All statuses</option><option value="draft">Draft</option><option value="published">Published and scheduled</option><option value="archived">Archived</option></select></label>
        </div>

        {state.status === "loading" && <div className="blog-admin-state" role="status"><span className="travel-admin-gate__spinner" aria-hidden="true" /><strong>Loading insurance posts...</strong></div>}
        {state.status === "error" && <div className="blog-admin-state blog-admin-state--error" role="alert"><strong>Unable to load posts</strong><span>{state.message}</span><button className="travel-admin-button" type="button" onClick={() => setReload((value) => value + 1)}>Try again</button></div>}
        {state.status === "ready" && state.data.results.length === 0 && <div className="blog-admin-state blog-admin-state--empty"><span className="blog-admin-state__icon" aria-hidden="true"><i className="far fa-newspaper" /></span><strong>{query || status ? "No posts match these filters" : "Your insurance library is ready"}</strong><span>{query || status ? "Try another search or status." : "Create the first insurance article when you are ready."}</span><Link className="travel-admin-button" to="/content-admin/blog/new">Create a post</Link></div>}
        {state.status === "ready" && state.data.results.length > 0 && <>
          <div className="travel-admin-table-wrap blog-admin-table-wrap"><table className="travel-admin-table blog-admin-table"><thead><tr><th>Story</th><th>Channel</th><th>Category</th><th>Status</th><th>Author</th><th>Actions</th></tr></thead><tbody>
            {state.data.results.map((post) => { const display = postStatus(post); return <tr key={post.id}>
              <td data-label="Story"><div className="blog-admin-story-cell"><span className="blog-admin-story-cell__image">{post.cover_image_url ? <img src={post.cover_image_url} alt="" /> : <i className="far fa-image" />}</span><div><strong>{post.title || "Untitled post"}</strong><span>/{post.slug}</span></div></div></td>
              <td data-label="Channel"><span className="blog-admin-channel" aria-label={`${channelLabel(post.channel)} channel`}><i className="fas fa-shield-heart" aria-hidden="true" />{channelLabel(post.channel)}</span></td>
              <td data-label="Category"><span className="blog-admin-category">{post.category || "Uncategorised"}</span></td>
              <td data-label="Status"><span className={`blog-admin-status blog-admin-status--${display.value}`}><i aria-hidden="true" />{display.label}</span><span className="blog-admin-date-note">Updated {formatDate(post.updated_at)}</span></td>
              <td data-label="Author"><strong>{post.author_name || "Editorial team"}</strong></td>
              <td data-label="Actions">{pendingDelete === post.id ? <div className="blog-admin-delete-confirmation" role="group" aria-label={`Delete ${post.title}`}><strong>Delete permanently?</strong><span>This cannot be undone.</span><div><button type="button" className="blog-admin-row-button blog-admin-row-button--danger" disabled={deleting === post.id} onClick={() => removePost(post)}>{deleting === post.id ? "Deleting..." : "Confirm delete"}</button><button type="button" className="blog-admin-row-button" disabled={deleting === post.id} onClick={() => setPendingDelete(null)}>Cancel</button></div></div> : <div className="blog-admin-row-actions"><button type="button" className="blog-admin-row-button" onClick={() => navigate(`/content-admin/blog/${post.id}/edit`)}><i className="fas fa-pen" aria-hidden="true" /> Edit</button>{post.status === "published" && <Link className="blog-admin-row-button" to={`/blog/${post.slug}`} target="_blank"><i className="fas fa-arrow-up-right-from-square" aria-hidden="true" /> View</Link>}<button type="button" className="blog-admin-row-button blog-admin-row-button--danger-text" onClick={() => removePost(post)}><i className="far fa-trash-can" aria-hidden="true" /> Delete</button></div>}</td>
            </tr>; })}
          </tbody></table></div>
          <footer className="travel-admin-pagination blog-admin-pagination"><p>Page {page} - {state.data.count.toLocaleString("en-NG")} posts</p><div><button type="button" disabled={!state.data.previous} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button><button type="button" disabled={!state.data.next} onClick={() => setPage((value) => value + 1)}>Next</button></div></footer>
        </>}
      </section>
    </div>
  );
};

export default BlogAdmin;
